import { useCallback, useMemo, useSyncExternalStore } from "react";
import type { Task, TaskId, Category, SubtaskId } from "../../../shared/types";
import * as taskStore from "../store/task-store";

export function useTasks() {
  const tasks = useSyncExternalStore(
    taskStore.subscribe,
    taskStore.getSnapshot,
  );

  const activeTasks = useMemo(() => tasks.filter((t) => !t.archived), [tasks]);
  const archivedTasks = useMemo(() => tasks.filter((t) => t.archived), [tasks]);

  return {
    tasks,
    activeTasks,
    archivedTasks,
    addTask: useCallback(
      (title: string, category: Category, dueDate: string | null = null, reminderMinutes: number | null = null, reminderTime: string | null = null, reminderType: "BEFORE" | "AT_TIME" | null = null) =>
        taskStore.addTask(title, category, dueDate, "normal", reminderMinutes, reminderTime, reminderType),
      [],
    ),
    updateTask: useCallback(
      (
        id: TaskId,
        updates: Partial<
          Pick<
            Task,
            "title" | "status" | "dueDate" | "priority" | "notes" | "tags" | "reminderMinutes" | "reminderTime" | "reminderType"
          >
        >,
      ) => taskStore.updateTask(id, updates),
      [],
    ),
    deleteTask: useCallback((id: TaskId) => taskStore.deleteTask(id), []),
    deleteTaskLocal: useCallback((id: TaskId) => taskStore.deleteTaskLocal(id), []),
    confirmDeleteTask: useCallback((id: TaskId) => taskStore.confirmDeleteTask(id), []),
    cycleStatus: useCallback((id: TaskId) => taskStore.cycleStatus(id), []),
    addSubtask: useCallback(
      (taskId: TaskId, title: string) => taskStore.addSubtask(taskId, title),
      [],
    ),
    toggleSubtask: useCallback(
      (taskId: TaskId, subtaskId: SubtaskId) =>
        taskStore.toggleSubtask(taskId, subtaskId),
      [],
    ),
    deleteSubtask: useCallback(
      (taskId: TaskId, subtaskId: SubtaskId) =>
        taskStore.deleteSubtask(taskId, subtaskId),
      [],
    ),
    archiveTask: useCallback((id: TaskId) => taskStore.archiveTask(id), []),
    reorderTask: useCallback(
      (taskId: TaskId, newIndex: number) =>
        taskStore.reorderTask(taskId, newIndex),
      [],
    ),
    importTasks: useCallback(
      (data: unknown[]) => taskStore.importTasks(data),
      [],
    ),
    undo: useCallback(() => taskStore.undo(), []),
    redo: useCallback(() => taskStore.redo(), []),
    canUndo: taskStore.canUndo(),
    canRedo: taskStore.canRedo(),
  };
}
